// Pre-fill a NEW configuration with its predicted answers (shown grey) before the form appears.
//
// Runs once, on a "Configure" launch only: an "Edit Config" launch restores the item's own
// configuration (AutoLoadItem) and is never predicted over. The form stays behind the loading
// screen until this calls onDone -- whether a prediction was applied, none existed, or the call
// failed -- so the user never sees blank answers turn grey under them.

import { useEffect, useRef } from "react";
import { useAnswers } from "../state/AnswersContext";
import { getLaunchContext } from "../util/launchContext";
import { fetchEvidence, fetchLaunchPrediction, fetchPo, toInput, toSource, type PoEvidence } from "../util/predictionClient";
import { describeFieldwise, loadModel, PO_READY_EVENT, predictFirstPage, prepareFieldwise, setPoEvidence } from "../util/fieldwiseRun";
import { toAnswers } from "../util/fieldwise";
import { launchKeys } from "../util/prediction";
import type { Question, Tab } from "../types";
import { takeOppositeSide } from "../util/pendingMirror";

export default function PredictLaunch({ onDone, questions, tabs }: { onDone: () => void; questions: Question[]; tabs: Tab[] }) {
  const { applyPrediction, refinePrediction, product, formCompleter } = useAnswers();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const ctx = getLaunchContext();
    if (ctx.itemId) {
      onDone();
      return;
    }
    // No unmount guard: the provider outlives this, and a dev StrictMode remount must not
    // strand the form behind the loading screen by swallowing onDone.
    (async () => {
      try {
        // "Configure the opposite side" (SaveSubmitButton) reloaded into this launch with the
        // mirror of the housing just saved.
        const opposite = takeOppositeSide(product, ctx.customerId);
        if (opposite) {
          await applyPrediction(toInput(opposite), toSource(opposite), opposite.mirror ?? null, { show: true }); // the rep asked for it
          return;
        }
        // FIELD BY FIELD (util/fieldwise.ts): the first page, answer by answer, from this
        // customer's and the user's past configurations. An MB housing's opposite side is still
        // the whole mirror. If the evidence or the model cannot be had, the whole-configuration
        // prediction below.
        // THE CUSTOMER'S PO (PO-PREDICTION.md): on a sales order, its PO is read beside the launch,
        // never awaited by it (uncached it takes seconds). If it is back by the time the first
        // page is predicted it is part of it; otherwise it refines the answers nobody has touched
        // when it lands.
        let poNow: PoEvidence | null = null;
        let poSettled = false;
        const poCall = ctx.originType === "SO" ? fetchPo(product, ctx.transactionId).catch(() => null) : null;
        poCall?.then((p) => { poNow = p; poSettled = true; });
        const [ev, model] = await Promise.all([fetchEvidence(product, ctx.customerId, ctx.transactionId, ctx.userName), loadModel()]);
        const complete = formCompleter();
        if (ev?.mirror) {
          await applyPrediction(toInput(ev.mirror), toSource(ev.mirror), ev.mirror.mirror ?? null);
          return;
        }
        // THE PART THE ORDER'S OWN QUOTE NAMES (2026-10-20, siblings.js quoteNamedPart): a fresh
        // order made from a quote whose burner line is already a cpq2 part. That is a repeat of
        // the part, housing and all, not a guess, so it loads whole and shown, and nothing
        // re-predicts over it (held) -- the same standing as a Recent pick. The PO, when it lands,
        // is still this order's own word and may correct it where nobody has touched an answer.
        if (ev?.named) {
          await applyPrediction(toInput(ev.named), toSource(ev.named), null, { held: true });
          return;
        }
        if (ev && model && ev.sources.length) {
          const fw = await prepareFieldwise(ev, model, questions, tabs, complete, { product, customerId: ctx.customerId ?? "", user: ctx.userName ?? "" });
          if (poSettled && setPoEvidence(poNow, fw.optionOrder)) window.setTimeout(() => window.dispatchEvent(new Event(PO_READY_EVENT)), 0);
          else if (poCall) {
            void poCall.then(async (p) => {
              if (!setPoEvidence(p, fw.optionOrder)) return;
              const again = predictFirstPage(fw);
              const d = describeFieldwise(fw);
              const only = launchKeys(questions, tabs);
              await refinePrediction(
                { answers: toAnswers(again.values, fw.optionsOf) },
                { id: "PO", name: d.name, customerName: null, createdAt: d.createdAt, basis: d.basis, kind: "fieldwise" },
                (k) => !only(k),
                { provenance: again.provenance }
              );
              // The pages after the first, if already predicted, are predicted again with it.
              window.dispatchEvent(new Event(PO_READY_EVENT));
            });
          }
          const c = predictFirstPage(fw);
          if (Object.keys(c.values).length) {
            const d = describeFieldwise(fw);
            await applyPrediction(
              { answers: toAnswers(c.values, fw.optionsOf) },
              { id: `F${ev.pickId ?? ""}`, name: d.name, customerName: null, createdAt: d.createdAt, basis: d.basis, kind: "fieldwise" },
              null,
              { only: launchKeys(questions, tabs), provenance: c.provenance }
            );
          }
          return;
        }
        const res = await fetchLaunchPrediction(product, ctx.customerId, ctx.transactionId);
        // The first page only, the housing left to the user (util/prediction launchKeys) --
        // except an MB mirror, which is the same housing by construction and loads whole.
        if (res) await applyPrediction(toInput(res), toSource(res), res.mirror ?? null, res.mirror ? undefined : { only: launchKeys(questions, tabs) });
      } catch (e) {
        console.warn("[cpq2] prediction not applied", e);
      } finally {
        onDone();
      }
    })();
    // Once per launch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
